import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Search } from 'lucide-react';
import { haptic } from '../../utils/haptics';

/**
 * Kopfzeile der Startseite (Skills entdecken, PLAN § 2.1 und Phase 3).
 *
 * Eine einzige Zeile, wie im abgestimmten Entwurf (docs/features/skills-entdecken/
 * entwurf-v1.html): links die Pillen „Personen | Skills“, rechts nur die Lupe als
 * runder 40-px-Knopf mit Rand. Bewusst NICHT im Kopf (Heiko, 16.10.2026): kein
 * Icon, kein sichtbarer Titel „Entdecken“, kein Rundenzähler „3 von 3“. Den Stand
 * der Runde zeigen Start- und Schlusskarte. Jede Zeile hier fehlt der Karte.
 *
 * Eigene Pillen statt `ui/SegmentedControl`: Der Entwurf zeigt zwei getrennte
 * Pillen, die aktive gefüllt in der Vordergrundfarbe. Das Segment ist eine graue
 * Spur mit heller Fläche darauf und gehört auf die Profilkarte. Die Tastatur-
 * bedienung ist dieselbe (Pfeile, Pos1/Ende, automatische Aktivierung).
 *
 * Tipp-Ziele: Pillen und Lupe sehen kleiner aus (30 bzw. 40 px), ihre Knöpfe
 * sind aber 44 px hoch (MOBILE_UX_GUIDE). Die Zeile ist damit 44 px hoch.
 *
 * Kein Lila: Ein Umschalter ist kein Aufruf zum Handeln, und Lila ist auf der
 * Startseite schon an Aura-Chip und aktiver Navigation vergeben (CD, max. 3×).
 */

export const DISCOVER_TABS = [
  { value: 'people', label: 'Personen' },
  { value: 'skills', label: 'Skills' },
];

export const discoverTabId = (idPrefix, value) => `${idPrefix}-tab-${value}`;
export const discoverPanelId = (idPrefix, value) => `${idPrefix}-panel-${value}`;

const SEARCH_LABELS = {
  people: 'Suche öffnen',
  skills: 'Skills durchsuchen',
};

const DiscoverHeader = ({
  tab,
  onTabChange,
  onSearch,
  searchExpanded = false,
  idPrefix = 'discover',
  className = '',
}) => {
  const refs = useRef([]);

  // Ein weicher Tick nur bei echtem Wechsel, nicht beim Tipp auf den aktiven
  // Reiter (Okt 2026). Die Telemetrie (BROWSE_TAB_OPENED) haengt am Aufrufer
  // und bleibt unberuehrt.
  const selectTab = (next) => {
    if (next !== tab) haptic?.selection?.();
    onTabChange(next);
  };

  const handleKeyDown = (event, index) => {
    const last = DISCOVER_TABS.length - 1;
    let next = null;
    if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    if (next === null) return;
    event.preventDefault();
    selectTab(DISCOVER_TABS[next].value);
    refs.current[next]?.focus();
  };

  // Im Reiter „Skills“ klappt die Lupe ein Feld auf; das sagt aria-expanded.
  // Im Reiter „Personen“ wechselt sie in die Suche, dort gibt es nichts aufzuklappen.
  const expandProps = tab === 'skills'
    ? { 'aria-expanded': searchExpanded, 'aria-controls': discoverPanelId(idPrefix, 'skills') }
    : {};

  return (
    <div className={`flex h-11 min-w-0 items-center gap-1.5 ${className}`}>
      {/* Der sichtbare Titel ist entfallen; Screenreader brauchen die Überschrift
          der Seite weiterhin. */}
      <h1 className="sr-only">Entdecken</h1>
      <div role="tablist" aria-label="Entdecken" className="flex min-w-0 items-center gap-1.5">
        {DISCOVER_TABS.map((option, index) => {
          const active = option.value === tab;
          return (
            <button
              key={option.value}
              ref={(el) => { refs.current[index] = el; }}
              type="button"
              role="tab"
              id={discoverTabId(idPrefix, option.value)}
              aria-selected={active}
              aria-controls={discoverPanelId(idPrefix, option.value)}
              tabIndex={active ? 0 : -1}
              onClick={() => selectTab(option.value)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              // Die Fläche ist der ganze 44-px-Knopf, die Pille sitzt darin.
              className="group flex h-11 shrink-0 items-center rounded-full touch-manipulation focus-visible:outline-none"
            >
              <span
                className={[
                  'inline-flex h-[30px] items-center rounded-full border px-3.5 text-[13px] font-semibold leading-none',
                  'transition-colors duration-fast',
                  'group-focus-visible:ring-2 group-focus-visible:ring-primary group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-background',
                  active
                    ? 'border-foreground bg-foreground text-surface'
                    : 'border-border bg-surface text-muted-foreground group-hover:text-foreground',
                ].join(' ')}
              >
                {option.label}
              </span>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onSearch}
        aria-label={SEARCH_LABELS[tab] || SEARCH_LABELS.people}
        {...expandProps}
        className="group ml-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-full touch-manipulation focus-visible:outline-none"
      >
        <span
          className={[
            'flex h-10 w-10 items-center justify-center rounded-full border border-border bg-surface text-foreground',
            'transition-colors duration-fast group-hover:bg-surface-raised',
            'group-focus-visible:ring-2 group-focus-visible:ring-primary group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-background',
          ].join(' ')}
        >
          <Search className="h-5 w-5" aria-hidden="true" />
        </span>
      </button>
    </div>
  );
};

DiscoverHeader.propTypes = {
  tab: PropTypes.oneOf(['people', 'skills']).isRequired,
  onTabChange: PropTypes.func.isRequired,
  /** Lupe: im Reiter Personen die Suche, im Reiter Skills das Filterfeld */
  onSearch: PropTypes.func.isRequired,
  /** Reiter Skills: ist das Filterfeld offen? (aria-expanded) */
  searchExpanded: PropTypes.bool,
  idPrefix: PropTypes.string,
  className: PropTypes.string,
};

export default DiscoverHeader;
